import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const copyText = async (value) => {
  try {
    await navigator.clipboard.writeText(String(value || ""));
    toast.success(i18n.t("financial.subscription.binanceCopied"));
  } catch {
    toast.error(i18n.t("financial.subscription.binanceCopyFail"));
  }
};

export default function InvoiceBinanceCheckoutDialog({ open, invoiceId, onClose, onPaid }) {
  const [checkout, setCheckout] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [billingWhatsapp, setBillingWhatsapp] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    api
      .get("/binance-pay/public")
      .then(({ data }) => setBillingWhatsapp(String(data?.billingWhatsapp || "")))
      .catch(() => setBillingWhatsapp(""));
  }, [open]);

  const refresh = async (silent = false) => {
    if (!invoiceId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/binance-poll`);
      setCheckout(data);
      if (data?.status === "paid") {
        toast.success(i18n.t("financial.subscription.binancePaid"));
        onPaid?.(data);
      }
    } catch (e) {
      if (!silent) {
        try {
          const { data } = await api.get(`/invoices/${invoiceId}/binance-checkout`);
          setCheckout(data);
        } catch (err) {
          toastError(err);
        }
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !invoiceId) return undefined;
    setCheckout(null);
    setOrderId("");
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post(`/invoices/${invoiceId}/binance-checkout`);
        setCheckout(data);
      } catch (e) {
        toastError(e);
      } finally {
        setLoading(false);
      }
    })();
    timer.current = setInterval(() => refresh(true), 15000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId]);

  const confirmOrder = async () => {
    if (!invoiceId || !orderId.trim()) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/binance-confirm`, {
        orderId: orderId.trim(),
      });
      setCheckout(data);
      toast.success(i18n.t("financial.subscription.binancePaid"));
      onPaid?.(data);
    } catch (e) {
      toastError(e);
    } finally {
      setConfirming(false);
    }
  };

  const amount = checkout?.amountUsdt != null ? Number(checkout.amountUsdt).toFixed(2) : "—";
  const paid = checkout?.status === "paid";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t("financial.subscription.binanceTitle")}</DialogTitle>
      <DialogContent dividers>
        {loading && !checkout ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              {checkout?.qrUrl ? (
                <img
                  src={checkout.qrUrl}
                  alt="QR Binance Pay"
                  style={{ width: 180, height: 180, objectFit: "contain", borderRadius: 8 }}
                />
              ) : null}
              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 12 }}>
                {amount} USDT
              </div>
              <div style={{ opacity: 0.75, marginTop: 4 }}>
                {i18n.t("financial.subscription.binanceExact")}
              </div>
            </div>
            {checkout?.payId ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Pay ID"
                  value={checkout.payId}
                  InputProps={{ readOnly: true }}
                />
                <Button size="small" onClick={() => copyText(checkout.payId)} startIcon={<FileCopyIcon />}>
                  {i18n.t("financial.subscription.binanceCopy")}
                </Button>
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="USDT"
                value={`${amount} USDT`}
                InputProps={{ readOnly: true }}
              />
              <Button size="small" onClick={() => copyText(amount)} startIcon={<FileCopyIcon />}>
                {i18n.t("financial.subscription.binanceCopy")}
              </Button>
            </div>
            {paid ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#2e7d32", fontWeight: 700 }}>
                <CheckCircleIcon />
                {i18n.t("financial.subscription.binancePaid")}
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {i18n.t("financial.subscription.binanceStepsTitle")}
                </div>
                <ol style={{ paddingLeft: 20, margin: "0 0 12px", fontSize: 14, lineHeight: 1.5 }}>
                  <li>{i18n.t("financial.subscription.binanceStep1")}</li>
                  <li>
                    {i18n.t("financial.subscription.binanceStep2", {
                      amount,
                      payId: checkout?.payId || "—",
                    })}
                  </li>
                  <li>{i18n.t("financial.subscription.binanceStep3")}</li>
                  <li>{i18n.t("financial.subscription.binanceStep4")}</li>
                </ol>
                <div style={{ opacity: 0.8, fontSize: 13, marginBottom: 12 }}>
                  {i18n.t("financial.subscription.binanceHint")}
                </div>
                {billingWhatsapp ? (
                  <div style={{ fontSize: 13, marginBottom: 12 }}>
                    {i18n.t("financial.subscription.binanceWhatsappAlt")}{" "}
                    <a
                      href={`https://wa.me/${billingWhatsapp}?text=${encodeURIComponent("PAGAR PLAN")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      +{billingWhatsapp}
                    </a>
                  </div>
                ) : null}
                <TextField
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  label={i18n.t("financial.subscription.binanceOrderId")}
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  helperText={i18n.t("financial.subscription.binanceOrderHint")}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t("financial.subscription.close")}</Button>
        {!paid ? (
          <>
            <Button startIcon={<RefreshIcon />} onClick={() => refresh(false)} disabled={loading}>
              {i18n.t("financial.subscription.binanceRefresh")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={confirmOrder}
              disabled={confirming || !orderId.trim()}
            >
              {confirming ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                i18n.t("financial.subscription.binanceConfirm")
              )}
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
